import { Card } from "@/components/ui/card";
import { RecordIcon } from "./components/RecordIcon";
import { EditableTitle } from "./components/EditableTitle";
import { RecordStatus } from "./components/RecordStatus";
import { RecordActions } from "./components/RecordActions";
import { TagsDisplaySection } from "./TagsDisplaySection";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import type { RecordHeaderProps } from "./types/recordHeader.types";
import type { Tag } from "@/integrations/supabase/types";

export const RecordHeader = ({
  record,
  activeTab,
  onCopy,
  onDownload,
  onReprocessSuccess,
  onTagsUpdated,
  onReprocess,
  onDelete,
  onManageTags,
  onNavigateToRecords,
  onEditTag,
  onDocumentDownload,
  onShare,
  generatingDoc = false,
  isPublished = false,
  shareUrl,
  disabled = false,
}: RecordHeaderProps) => {
  const navigate = useNavigate();
  const [copying, setCopying] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);

  const handleNavigateToTemplate = (templateId: string) => {
    navigate(`/templates/edit/${templateId}`);
  };

  const handleTitleUpdate = (newTitle: string) => {
    // Optimistically update the record title in parent component
    // This would typically trigger a refresh or re-fetch of the record data
    console.log('Record title updated to:', newTitle);
  };

  const handleTitleEditStart = () => {
    setIsTitleEditing(true);
  };

  const handleTitleEditEnd = () => {
    setIsTitleEditing(false);
  };

  const handleCopy = async () => {
    setCopying(true);
    await onCopy();
    setTimeout(() => setCopying(false), 1500);
  };

  const handleReprocess = () => {
    onReprocess?.();
  };

  const handleDelete = () => {
    onDelete?.();
  };

  const handleManageTags = () => {
    onManageTags?.();
  };

  const handleNavigateToRecords = (tag: Tag) => {
    onNavigateToRecords?.(tag);
  };

  const handleEditTag = () => {
    onEditTag?.();
  };

  const isCompleted = record.processingStatus === "completed" || record.status === "completed";

  return (
    <div>
      <Card className="overflow-hidden rounded-b-none border-b-0 border border-border/50">
        <div className="relative border-l-4 border-brand-500 bg-gradient-to-r from-brand-50/20 to-transparent">
          <div className="p-4 sm:p-5">
            {/* Main content area with modular components */}
            <div className="flex items-start justify-between gap-3 sm:gap-4">
              <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                <RecordIcon duration={record.duration} className="flex-shrink-0" />
                <EditableTitle
                  record={record}
                  onNavigateToTemplate={handleNavigateToTemplate}
                  onTitleUpdate={handleTitleUpdate}
                  onEditStart={handleTitleEditStart}
                  onEditEnd={handleTitleEditEnd}
                  isPublished={isPublished}
                  shareUrl={shareUrl}
                  disabled={disabled}
                />
              </div>

              {/* Hide status during title editing for better mobile UX */}
              {!isTitleEditing && !isCompleted && (
                <RecordStatus 
                  status={record.processingStatus || record.status || ""}
                  date={record.createdAt}
                  className="flex-shrink-0 pt-0.5 sm:pt-0"
                />
              )}
            </div>

            {/* Tags section */}
            <div className="mt-4">
              <TagsDisplaySection
                tags={record.tags || []}
                onManageClick={handleManageTags}
                onNavigateToRecords={handleNavigateToRecords}
                onEditTag={handleEditTag}
                className="min-w-0"
                disabled={disabled}
              />
            </div>

            {/* Actions section */}
            <RecordActions
              record={record}
              activeTab={activeTab}
              onCopy={handleCopy}
              onDownload={onDownload}
              onReprocess={handleReprocess}
              onDelete={handleDelete}
              onShare={onShare}
              copying={copying}
              generatingDoc={generatingDoc}
              isCompleted={isCompleted}
              onDocumentDownload={onDocumentDownload}
              disabled={disabled}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
